'use client';

import * as React from 'react';
import { DollarSignIcon, UsersIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';

import { Card, CardContent } from '@workspace/ui/components/card';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { cn } from '@workspace/ui/lib/utils';

interface StatCardsProps {
  stats: {
    avgTotalSpend: number;
    medianTotalSpend: number;
    minTotalSpend: number;
    maxTotalSpend: number;
    avgGuestCount: number;
  };
  filteredCount: number;
  isLoading?: boolean;
}

export function StatCards({ stats, filteredCount, isLoading }: StatCardsProps): React.JSX.Element {
  const cards = [
    {
      title: 'Average Cost',
      value: `$${stats.avgTotalSpend.toLocaleString()}`,
      subtitle: `Median: $${stats.medianTotalSpend.toLocaleString()}`,
      icon: DollarSignIcon,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    },
    {
      title: 'Budget Range',
      value: `$${(stats.minTotalSpend / 1000).toFixed(0)}k - $${(stats.maxTotalSpend / 1000).toFixed(0)}k`,
      subtitle: `Across ${filteredCount.toLocaleString()} weddings`,
      icon: TrendingUpIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    },
    {
      title: 'Avg Guest Count',
      value: stats.avgGuestCount.toString(),
      subtitle: 'Per wedding',
      icon: UsersIcon,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50 dark:bg-violet-950/30',
    },
    {
      title: 'Cost Per Guest',
      value: stats.avgGuestCount > 0
        ? `$${Math.round(stats.avgTotalSpend / stats.avgGuestCount).toLocaleString()}`
        : '$0',
      subtitle: 'On average',
      icon: TrendingDownIcon,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.title} className="overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                {isLoading ? (
                  <Skeleton className="h-7 w-24" />
                ) : (
                  <p className="text-2xl font-bold tracking-tight">{card.value}</p>
                )}
                {isLoading ? (
                  <Skeleton className="h-4 w-32" />
                ) : (
                  <p className="text-xs text-muted-foreground">{card.subtitle}</p>
                )}
              </div>
              <div className={cn('p-2 rounded-lg', card.bgColor)}>
                <card.icon className={cn('h-5 w-5', card.color)} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

